import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import { useYbrCrossFlowSuppression } from '@jira/yellow-brick-road-utils/src/controllers/useYbrCrossFlowSuppression.tsx';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { UpdateProjectsProvider } from '@atlassian/jira-navigation-apps-common/src/controllers/update-project-context/index.tsx';
import {
	ENTITY_ID,
	MENU_ID_PROJECTS,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useRefetchWhenIsSelected } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/controller/use-refetch-when-is-selected/index.tsx';
import { useSubscribeAndUpdateFavorites } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/services/set-favorite/index.tsx';
import { useStableItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/utils/use-stable-items/index.tsx';
import { useSyncChangeFavourites } from '@atlassian/jira-navigation-apps-sidebar-nav4-projects-customise-order/src/services/sync-change-favourites/useSyncChangeFavourites.tsx';
import { ViewOptions } from '@atlassian/jira-navigation-apps-sidebar-nav4-display-settings/src/ui/change-view-menu-item/view-options/constants.tsx';
import {
	useSidebarSettingsAri,
	useOwnerAri,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-display-settings/src/services/ari/use-ari.tsx';
import type { ProjectToRestore } from '@atlassian/jira-navigation-project-action-menu/src/types.tsx';
import ProjectRestoreModal from '@atlassian/jira-project-restore-modal/src/ui/index.tsx';
import type { Nav4ProjectsContent$key } from '@atlassian/jira-relay/src/__generated__/Nav4ProjectsContent.graphql';
import QUERY, {
	type Nav4ProjectsContentQuery,
	type Nav4ProjectsContentQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/Nav4ProjectsContentQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useProjectsAnalyticsL1 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l1/index.tsx';
import { Nav4Recommendations } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-growth-recommendations/src/index.tsx';
import { Nav4ProjectsSectionPlacement } from '@atlassian/jira-growth-nav-4-projects-section/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { Nav4ProjectMenuItem } from '../project-menu-item/Nav4ProjectMenuItem.tsx';
import { Nav4ProjectsEmptyContent } from '../empty-content/Nav4ProjectsEmptyContent.tsx';
import { Footer } from '../footer/index.tsx';
import { Nav4ProjectsSections } from './Nav4ProjectsSections.tsx';
import { Nav4ProjectsSectionsWithDisplaySettings } from './Nav4ProjectsSectionsWithDisplaySettings.tsx';

const MAX_RECENT_PROJECTS = 5;
export const MAX_FAVORITE_PROJECTS = 15;

type Nav4ProjectsContentProps = {
	queryRef: Nav4ProjectsContent$key;
};

function Nav4ProjectsContentInternal({ queryRef }: Nav4ProjectsContentProps): React.JSX.Element {
	const cloudId = useCloudId();
	const { getStableItems } = useStableItems();
	const { isSelectedPath } = useSidebarNav4();
	const [projectToRestore, setProjectToRestore] = useState<ProjectToRestore | null>(null);
	const { sendProjectMenuContentAnalyticsEvent } = useProjectsAnalyticsL1();
	const { shouldSuppress } = useYbrCrossFlowSuppression();

	useEffect(() => {
		sendProjectMenuContentAnalyticsEvent();
	}, [sendProjectMenuContentAnalyticsEvent]);

	// https://jplat.jira.atlassian.cloud/browse/BLU-2719
	// We intentionally request 10 starred and 15 recent projects.
	// Then, if all 10 starred projects were recently visited, we still show 5 recent projects as well.
	const data = useRefetchableFragment<Nav4ProjectsContentQuery, Nav4ProjectsContent$key>(
		graphql`
			fragment Nav4ProjectsContent on Query
			@refetchable(queryName: "Nav4ProjectsContentQuery")
			@argumentDefinitions(
				currentURL: { type: URL }
				nav4ProjectsSectionRecommendationEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/grw-1672-display-targeted-jsm-template-cta-in-nav4.relayprovider"
				}
				displaySettingsAPart1Enabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/blu-6315-display-settings-a-part-1.relayprovider"
				}
				displaySettingsPostGaEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/blu-6987-display-settings-post-ga-experiment.relayprovider"
				}
				jiraProjectsSidebarMenuEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/relay-migration-projects-fields-nav4-sidebar.relayprovider"
				}
			) {
				...Nav4ProjectsSectionsWithDisplaySettings
					@arguments(entityAri: $entityAriProjects, ownerAri: $ownerAri)
					@include(if: $displaySettingsAPart1Enabled)
					@skip(if: $displaySettingsPostGaEnabled)
				jira @required(action: THROW) {
					canCreateProject: canPerform(type: CREATE_PROJECT, cloudId: $cloudId)
						@optIn(to: "JiraAction")
						@include(if: $nav4ProjectsSectionRecommendationEnabled)
					favouriteProjects: favourites(
						cloudId: $cloudId
						filter: { type: PROJECT, sort: { order: DESC } }
						first: 15
					) @required(action: THROW) @skip(if: $jiraProjectsSidebarMenuEnabled) {
						__id
						pageInfo {
							hasNextPage
						}
						edges {
							node @required(action: THROW) {
								... on JiraProject {
									id @required(action: THROW)
									projectKey: key @required(action: THROW)
									...Nav4ProjectMenuItem
								}
							}
						}
					}
					recentProjects(cloudId: $cloudId, currentURL: $currentURL, first: 20)
						@required(action: THROW)
						@skip(if: $jiraProjectsSidebarMenuEnabled) {
						__id
						edges {
							node @required(action: THROW) {
								... on JiraProject {
									id @required(action: THROW)
									projectKey: key @required(action: THROW)
									...Nav4ProjectMenuItem
								}
							}
						}
					}
					jwmLicensing(cloudId: $cloudId) {
						currentUserSeatEdition
					}
				}
				jira_projectsSidebarMenu(cloudId: $cloudId, currentURL: $currentURL)
					@required(action: THROW)
					@include(if: $jiraProjectsSidebarMenuEnabled)
					@optIn(to: "JiraSidebarMenu") {
					mostRecent {
						id @required(action: THROW)
						projectKey: key @required(action: THROW)
						...Nav4ProjectMenuItem
					}
					mostRecentFromHistory @optIn(to: "JiraProjectMostRecentFromHistory") {
						id @required(action: THROW)
						projectKey: key @required(action: THROW)
						isFavourite
						...Nav4ProjectMenuItem
					}
					favourites(first: 15) @required(action: THROW) {
						__id
						pageInfo {
							hasNextPage
						}
						edges {
							node @required(action: THROW) {
								id @required(action: THROW)
								projectKey: key @required(action: THROW)
								...Nav4ProjectMenuItem
							}
						}
					}
					recents(first: 20) @required(action: THROW) {
						__id
						edges {
							node @required(action: THROW) {
								id @required(action: THROW)
								projectKey: key @required(action: THROW)
								...Nav4ProjectMenuItem
							}
						}
					}
					hasMore
					favouriteLimit
					recentLimit
					displayMode
				}
			}
		`,
		queryRef,
	);

	const [{ jira, jira_projectsSidebarMenu: projectsSidebarMenu }, refetch] = data;

	const { jwmLicensing, canCreateProject } = jira;

	const favouriteLimit = expVal('blu-6987-display-settings-post-ga-experiment', 'isEnabled', false)
		? projectsSidebarMenu?.favouriteLimit
		: MAX_FAVORITE_PROJECTS;

	const recentLimit = expVal('blu-6987-display-settings-post-ga-experiment', 'isEnabled', false)
		? projectsSidebarMenu?.recentLimit
		: MAX_RECENT_PROJECTS;

	const favouriteProjects = isJiraProjectsSidebarMenuEnabled()
		? projectsSidebarMenu?.favourites
		: jira.favouriteProjects;

	const recentProjects = isJiraProjectsSidebarMenuEnabled()
		? projectsSidebarMenu?.recents
		: jira.recentProjects;

	// throwing here enforces the mutually exclusive @include/@skip directives which otherwise would be handled by @required
	if (!favouriteProjects || !recentProjects) {
		throw new Error(
			'JiraProjectConnection is missing in Nav4ProjectsContent for favourite or recent projects',
		);
	}

	const mostRecentProject = projectsSidebarMenu?.mostRecent;

	/** Using `mostRecentFromHistory` for the cases where we don't send `currentURL` (see L1MenuItemsQuery)
	 * as a temporary fix to correctly display the projects in the sidebar, by checking the `displayMode`
	 * and adding `mostRecentFromHistory` to the lists if necessary
	 * More info here: https://jplat.atlassian.net/browse/BLU-6987 */
	const mostRecentFromHistory = projectsSidebarMenu?.mostRecentFromHistory;
	const displayMode = projectsSidebarMenu?.displayMode;
	const isMostRecentFromHistorySelected =
		mostRecentFromHistory && isSelectedPath(ENTITY_ID.PROJECT(mostRecentFromHistory.projectKey));

	const entityAriProjects = useSidebarSettingsAri('projects');
	const ownerAri = useOwnerAri();

	const queryVariables = useMemo(
		() => ({ cloudId, entityAriProjects, ownerAri }),
		[cloudId, entityAriProjects, ownerAri],
	);

	useRefetchWhenIsSelected<Nav4ProjectsContent$key, Nav4ProjectsContentQuery$variables>({
		cloudId,
		isSelectedPath,
		QUERY,
		refetch,
		menuId: MENU_ID_PROJECTS,
		queryVariables,
	});

	const refetchOnChange = useCallback(
		({ onComplete }: { onComplete: () => void }) => {
			refetch(queryVariables, {
				fetchPolicy: 'store-and-network',
				onComplete,
			});
		},
		[queryVariables, refetch],
	);
	useSubscribeAndUpdateFavorites({
		entityTypename: 'JiraProject',
		favouriteConnectionId: favouriteProjects.__id,
		recentConnectionId: recentProjects.__id,
		refetch: refetchOnChange,
	});

	useSyncChangeFavourites();

	const favorites = useMemo(() => {
		const favoritesList = favouriteProjects.edges?.filter(Boolean).map((edge) => edge.node) ?? [];

		const isMostRecentFromHistoryInFavoritesList = favoritesList.some(
			(project) => project.id === mostRecentFromHistory?.id,
		);
		const shouldShowStarredSection =
			displayMode === ViewOptions.STARRED_AND_RECENT || displayMode === ViewOptions.STARRED;
		const shouldAddMostRecentFromHistoryToFavouritesList =
			isJiraProjectsSidebarMenuEnabled() &&
			mostRecentFromHistory &&
			mostRecentFromHistory.isFavourite &&
			shouldShowStarredSection &&
			isMostRecentFromHistorySelected &&
			favoritesList.length > 0 &&
			!isMostRecentFromHistoryInFavoritesList;

		/** `currentURL` is not sent in L1MenuItemsQuery which might re-write projects wrong in the sidebar in some cases
		 * i.e. when the user visits Starred project which order # is more than the starred limit
		 * so the project should be displayed as the last Starred one
		 * With this fix we keep the sidebar in the correct state according to the current route performing
		 * the same logic that BE does when it is aware of the `currentURL` */
		if (shouldAddMostRecentFromHistoryToFavouritesList) {
			favoritesList.pop();
			favoritesList.push(mostRecentFromHistory);
		}

		const ids = favoritesList.map((project) => project.id).filter(Boolean) ?? [];

		const list =
			favoritesList.map((project) => {
				return (
					<Nav4ProjectMenuItem
						key={project.projectKey}
						queryRef={project}
						isCorePremiumUserSeat={jwmLicensing?.currentUserSeatEdition === 'PREMIUM'}
						setProjectToRestore={setProjectToRestore}
					/>
				);
			}) ?? [];

		const hasNextPage =
			favouriteProjects.pageInfo?.hasNextPage ||
			(favouriteLimit ? list.length > favouriteLimit : false);

		return {
			idSet: new Set(ids),
			list: list.slice(0, favouriteLimit ?? Infinity),
			hasItems: Boolean(list.length),
			hasNextPage,
		};
	}, [
		favouriteProjects,
		jwmLicensing,
		favouriteLimit,
		displayMode,
		mostRecentFromHistory,
		isMostRecentFromHistorySelected,
	]);

	const recentItems = useMemo(() => {
		const isStarredOnlyMode = displayMode === ViewOptions.STARRED;
		const shouldAddMostRecentFromHistoryToRecentList =
			isJiraProjectsSidebarMenuEnabled() &&
			mostRecentFromHistory &&
			!mostRecentFromHistory.isFavourite &&
			isStarredOnlyMode &&
			isMostRecentFromHistorySelected &&
			(recentProjects.edges?.length ?? 0) === 0;

		/** `currentURL` is not sent in L1MenuItemsQuery which might re-write projects wrong in the sidebar in some cases
		 * i.e. when Display Mode is STARRED only and the user visits a Recent project
		 * so the project should be displayed as the only one in the Recent section
		 * With this fix we keep the sidebar in the correct state according to the current route performing
		 * the same logic that BE does when it is aware of the `currentURL` */
		const nodes = shouldAddMostRecentFromHistoryToRecentList
			? [mostRecentFromHistory]
			: (recentProjects.edges
					?.filter(Boolean)
					.filter((edge) => edge.node.id && !favorites.idSet.has(edge.node.id))
					.map((edge) => edge.node)
					.slice(0, recentLimit ?? Infinity) ?? []);

		const stableOrderedNodes = getStableItems({ nodes, idKey: 'id' });

		const list = stableOrderedNodes.map((node) => {
			return (
				<Nav4ProjectMenuItem
					key={node.projectKey}
					queryRef={node}
					isCorePremiumUserSeat={jwmLicensing?.currentUserSeatEdition === 'PREMIUM'}
					setProjectToRestore={setProjectToRestore}
				/>
			);
		});

		return { list, hasItems: Boolean(list.length) };
	}, [
		recentProjects.edges,
		getStableItems,
		favorites.idSet,
		jwmLicensing?.currentUserSeatEdition,
		recentLimit,
		displayMode,
		mostRecentFromHistory,
		isMostRecentFromHistorySelected,
	]);

	const mostRecentProjectItem = useMemo(
		() =>
			mostRecentProject ? (
				<Nav4ProjectMenuItem
					key={mostRecentProject.projectKey}
					queryRef={mostRecentProject}
					isCorePremiumUserSeat={jwmLicensing?.currentUserSeatEdition === 'PREMIUM'}
					setProjectToRestore={setProjectToRestore}
				/>
			) : null,
		[mostRecentProject, jwmLicensing?.currentUserSeatEdition],
	);

	const restoreProjectModal = useCallback(() => {
		if (projectToRestore) {
			return (
				<ModalTransition>
					<ProjectRestoreModal
						{...projectToRestore}
						onProjectRestoreSuccess={() => {
							setProjectToRestore(null);
							refetch(queryVariables, {
								fetchPolicy: 'store-and-network',
							});
						}}
						onProjectRestoreFailure={() => {
							setProjectToRestore(null);
						}}
						onClose={() => setProjectToRestore(null)}
					/>
				</ModalTransition>
			);
		}
	}, [queryVariables, projectToRestore, refetch]);

	const recommendations = useMemo(() => {
		if (fg('sqa-1995_move_nav4recommendations_into_footer')) {
			return null;
		}
		if (shouldSuppress()) {
			return null;
		}
		if (
			!!canCreateProject &&
			expValEquals('grw-1672_display_targeted_jsm_template_cta_in_nav4', 'cohort', 'variation')
		) {
			return <Nav4ProjectsSectionPlacement />;
		}
		return <Nav4Recommendations />;
	}, [canCreateProject, shouldSuppress]);

	if (!favorites.hasItems && !recentItems.hasItems && !mostRecentProject) {
		return (
			<>
				<Nav4ProjectsEmptyContent />
				{recommendations}
				{restoreProjectModal()}
				{expVal('blu-6987-display-settings-post-ga-experiment', 'isEnabled', false) && (
					<Footer hasMore={projectsSidebarMenu?.hasMore} canCreateProject={canCreateProject} />
				)}
			</>
		);
	}

	return (
		<UpdateProjectsProvider
			favouriteConnectionId={favouriteProjects.__id}
			recentConnectionId={recentProjects.__id}
		>
			{!expVal('blu-6987-display-settings-post-ga-experiment', 'isEnabled', false) &&
			expVal('blu-6315-display-settings-a-part-1', 'isEnabled', false) ? (
				<Nav4ProjectsSectionsWithDisplaySettings
					favoriteItems={favorites}
					recentItems={recentItems}
					queryRef={data[0]}
					canCreateProject={canCreateProject}
				/>
			) : (
				<Nav4ProjectsSections
					favoriteItems={favorites}
					recentItems={recentItems}
					mostRecentProjectItem={mostRecentProjectItem}
					canCreateProject={canCreateProject}
				/>
			)}
			{recommendations}
			{restoreProjectModal()}
			{expVal('blu-6987-display-settings-post-ga-experiment', 'isEnabled', false) && (
				<Footer hasMore={projectsSidebarMenu?.hasMore} canCreateProject={canCreateProject} />
			)}
		</UpdateProjectsProvider>
	);
}

export const Nav4ProjectsContent = memo(Nav4ProjectsContentInternal);

function isJiraProjectsSidebarMenuEnabled() {
	return (
		expVal('blu-6987-display-settings-post-ga-experiment', 'isEnabled', false) ||
		expVal('blu-6314-drag-and-drop-projects-sidebar-experiment', 'isEnabled', false)
	);
}
